import PropTypes from 'prop-types';
import React, { Component } from 'react';
import cx from 'classnames';
import { defineMessages, intlShape } from 'react-intl';
import BodyClassName from 'react-body-classname';
import { Metadata } from 'components/Metadata/Metadata';
import SCHEDULE_CONTEXT from 'constants/schedule-context';
import { BASE_URL } from 'constants/urls';
import { ec } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import { ReferModalContainer } from 'features/Refer/ReferModalContainer';
import { SEARCH_PATH } from 'features/Search/Search.route.path';
import { VerticalsFilterContainer } from './filters/VerticalsFilterContainer';
import { SecondaryFiltersContainer } from './filters/SecondaryFiltersContainer';
import { SearchBoxContainer } from './SearchBox/SearchBoxContainer';
import { SearchResultsContainer } from './SearchResultsContainer';
import { ResultTypeSelectorContainer } from './ResultTypeSelectorContainer';
import { SearchMapContainer } from './Map/SearchMapContainer';
import { DateFilterContainer } from './filters/DateFilterContainer';
import { RESULT_TYPES } from './Search.constants';
import css from './Search.scss';

const messages = defineMessages({
  defaultTitle: {
    id: 'Search.pageTitle',
    defaultMessage: 'Search | ClassPass',
  },
  titleWithDescription: {
    id: 'Search.pageTitleWithDescription',
    defaultMessage: 'Best {searchDescription} | ClassPass',
  },
});

export class Search extends Component {
  componentDidMount() {
    segment.track('Search - View');
    ec('Search - View');
  }

  componentWillUnmount() {
    this.props.setScheduleContext(SCHEDULE_CONTEXT.CURRENT_CYCLE);
  }

  render() {
    const {
      includeSearchBox,
      hasStackedSearchBoxes,
      intl,
      isInVenueSupply,
      isMapView,
      isMobile,
      isMapToggleable,
      isLoggedIn,
      resultType,
      filterMetadata,
    } = this.props;
    const isMobileMapView = isMapToggleable && isMapView;
    const {
      header1: searchDescription,
      canonical_hash: canonicalHash,
      location_descriptor: locationDescriptor,
      activity_descriptor: activityDescriptor,
    } = filterMetadata;

    const title = searchDescription
      ? intl.formatMessage(messages.titleWithDescription, { searchDescription })
      : intl.formatMessage(messages.defaultTitle);
    const hasDateFilter = resultType !== RESULT_TYPES.VENUE && isMobile;

    let canonicalUrl;
    if (canonicalHash) {
      if (locationDescriptor && activityDescriptor) {
        canonicalUrl = `${BASE_URL}${SEARCH_PATH}/${locationDescriptor}/${activityDescriptor}/${canonicalHash}`;
      } else {
        canonicalUrl = `${BASE_URL}${SEARCH_PATH}/${canonicalHash}`;
      }
    }

    return (
      <BodyClassName className="body--header-no-shadow body--no-footer body--auto-min-height">
        <div
          className={cx(css.Search, {
            [css['Search--stackedSearchboxes']]: hasStackedSearchBoxes,
            [css['Search--venue-supply']]: isInVenueSupply,
          })}>
          <Metadata title={title} description={searchDescription} canonicalUrl={canonicalUrl} />
          <ReferModalContainer />
          <div className={css.Search__filters}>
            {/* TODO: remove 'SearchBoxContainer' code here since we can update logic in 'AppHeader' to
                      show the search box below the header content on the search page */}
            {includeSearchBox && <SearchBoxContainer shouldShowViewToggler />}
            <div
              className={cx('unified-filters', {
                pb0: hasDateFilter,
              })}>
              <div className="unified-filters__wrap">
                <VerticalsFilterContainer />
                <SecondaryFiltersContainer />
                {!isLoggedIn && !isMapToggleable && (
                  <div className="unified-filters__result-selector">
                    <ResultTypeSelectorContainer hideOptions extraClass="pv-small" />
                  </div>
                )}
              </div>
              {hasDateFilter && <DateFilterContainer />}
            </div>
          </div>
          <div className={css.Search__results__wrap}>
            {isMobileMapView ? __CLIENT__ && <SearchMapContainer /> : <SearchResultsContainer />}
          </div>
        </div>
      </BodyClassName>
    );
  }
}

Search.propTypes = {
  hasStackedSearchBoxes: PropTypes.bool,
  includeSearchBox: PropTypes.bool,
  intl: intlShape.isRequired,
  isInVenueSupply: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isMapToggleable: PropTypes.bool,
  isMapView: PropTypes.bool,
  isMobile: PropTypes.bool,
  resultType: PropTypes.oneOf(Object.keys(RESULT_TYPES)),
  setScheduleContext: PropTypes.func.isRequired,
  searchDescription: PropTypes.string,
  canonicalHash: PropTypes.string,
  filterMetadata: PropTypes.shape({
    header1: PropTypes.string,
    canonical_hash: PropTypes.string,
    location_descriptor: PropTypes.string,
    activity_descriptor: PropTypes.string,
  }).isRequired,
};
